import type { AuctisyncSupabase } from './client.js';

/**
 * Live Activity token plumbing (P1.3).
 *
 * iOS-only in practice, but the calls themselves are ordinary data operations,
 * so they live here rather than in `apps/bidder` — the Operator app needs the
 * same two calls for its close-monitoring Activity (P4.3), and here they are
 * testable under vitest instead of jest-expo.
 *
 * Both functions follow the `placeBid` contract: they never throw. They run
 * from ActivityKit event callbacks, where a rejected promise has no caller to
 * catch it and would surface as an unhandled rejection rather than as anything
 * the bidder could act on. Failures come back as structured results instead.
 */

/**
 * Why an Activity's token stopped being useful — the subset the CLIENT is
 * entitled to report.
 *
 * The table's CHECK also permits `token_dead` and `send_failed_persistent`, but
 * those are conclusions only the sender can reach (APNs said 410; N pushes
 * failed in a row). Leaving them out of this type means a client cannot claim
 * a token is dead and take it out of the fan-out.
 */
export type ClientRetireReason = 'activity_ended' | 'user_revoked';

export interface RegisterLiveActivityTokenInput {
  lotId: string;
  /** ActivityKit's identifier for this Activity, from `startActivity`. */
  activityId: string;
  /** The per-Activity APNs token, from the `addActivityTokenListener` event. */
  activityPushToken: string;
  /**
   * Which APP started the Activity: the running build's bundle id (per-house apps,
   * 2026-10-15). The server updates the Lock Screen under that app's own Apple
   * team and key; absent means AuctiSync's own app. Omitted from the request when
   * absent.
   */
  bundleId?: string;
}

export interface RetireLiveActivityTokenInput {
  activityId: string;
  reason: ClientRetireReason;
}

export interface LiveActivityTokenResult {
  ok: boolean;
  /** Machine-readable failure code; absent on success. */
  reason?: string;
  message?: string;
}

/** Safely read `.message` off a value the caller has not proven is an Error. */
function messageFrom(error: unknown): string | undefined {
  const message: unknown = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * Best-effort extraction of the EF's own error code out of a Supabase
 * FunctionsHttpError, which carries the raw Response on `context`.
 *
 * The distinction matters to the caller: `unknown_lot` is permanent and must
 * not be retried, while a 5xx is worth another attempt on the next token event.
 * Wrapped defensively — this is error-path-of-an-error-path, and failing to
 * parse the body must not itself throw.
 */
async function errorCodeFrom(error: unknown, fallback: string): Promise<string> {
  const context: unknown = (error as { context?: unknown } | null)?.context;
  if (!context || typeof (context as Response).json !== 'function') return fallback;
  try {
    const body: unknown = await (context as Response).json();
    const code = (body as { error?: unknown } | null)?.error;
    return typeof code === 'string' && code.length > 0 ? code : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Hand ActivityKit's per-Activity push token to the server so bid updates can
 * reach this Activity.
 *
 * Goes through the `register-live-activity-token` EF rather than writing the
 * table directly: `user_id` has to come from the verified JWT subject and
 * `tenant_id` from the lot, neither of which a client can be trusted to supply.
 * Re-registering the same token is safe and idempotent — the EF upserts on the
 * token and clears any prior soft-delete.
 */
export async function registerLiveActivityToken(
  supabase: AuctisyncSupabase,
  input: RegisterLiveActivityTokenInput,
): Promise<LiveActivityTokenResult> {
  try {
    const res = await supabase.functions.invoke<{ ok?: boolean }>('register-live-activity-token', {
      body: {
        lot_id: input.lotId,
        activity_id: input.activityId,
        activity_push_token: input.activityPushToken,
        ...(input.bundleId === undefined ? {} : { bundle_id: input.bundleId }),
      },
    });

    // functions-js declares FunctionsResponseFailure.error as `any`, so no
    // response generic can tighten it. Narrow it to `unknown` at the boundary
    // and read it through guards rather than letting `any` leak inward.
    const invokeError: unknown = res.error;
    if (invokeError) {
      const message = messageFrom(invokeError);
      return {
        ok: false,
        reason: await errorCodeFrom(invokeError, 'invoke_failed'),
        // Spread rather than assign: under exactOptionalPropertyTypes an
        // absent message must be an absent KEY, not a key holding undefined.
        ...(message === undefined ? {} : { message }),
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: 'invoke_failed',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Soft-delete this Activity's token so the sender stops pushing to an Activity
 * that is no longer on screen.
 *
 * Written directly rather than through an EF: the row belongs to the caller,
 * and the `live_activity_tokens_own` RLS policy already restricts the update to
 * `user_id = auth.uid()`. There is no server-side judgement to add, so an EF
 * would only be a hop.
 *
 * Scoped to rows that are still active. Without that, an Activity ending
 * moments after the sender retired the same row for `token_dead` would relabel
 * a diagnosed dead token as a routine ending, losing the only signal we get
 * that APNs rejected it.
 */
export async function retireLiveActivityToken(
  supabase: AuctisyncSupabase,
  input: RetireLiveActivityTokenInput,
): Promise<LiveActivityTokenResult> {
  try {
    const { error } = await supabase
      .from('live_activity_tokens')
      .update({ disabled_at: new Date().toISOString(), disabled_reason: input.reason })
      .eq('activity_id', input.activityId)
      .is('disabled_at', null);

    if (error) return { ok: false, reason: 'update_failed', message: error.message };
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: 'update_failed',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
